import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { X, User, Phone, MapPin, ShoppingBag } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import { useCartStore, CartItem } from '../../core/stores/cartStore'
import { useMenuStore } from '../../core/stores/menuStore'
import { OrderType } from '../../core/enums/OrderType'
import { AppRoute } from '../../core/config/app-routes'
import Input from '../ui/Input'
import Button from '../ui/Button'

interface CustomerInfoModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

export default function CustomerInfoModal({ isOpen, onClose, onConfirm }: CustomerInfoModalProps) {
  const { isRTL, t } = useTranslation()
  const { color, darker, opacity15 } = useBusinessColor()
  const { items, clearCart } = useCartStore()
  const { menu } = useMenuStore()
  const { slug } = useParams()
  
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerAddress, setCustomerAddress] = useState('')
  const [orderType, setOrderType] = useState<OrderType>(OrderType.DELIVERY)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const businessName = menu?.business?.name || ''
  const businessPhone = menu?.business?.contactNumber || ''

  const orderTypeOptions = [
    { value: OrderType.DELIVERY, label: t('orderTypeDelivery') },
    { value: OrderType.PICKUP, label: t('orderTypePickup') }
  ]

  const getOrderTypeLabel = (type: OrderType) => {
    switch (type) {
      case OrderType.DELIVERY:
        return t('orderTypeDelivery')
      case OrderType.PICKUP:
        return t('orderTypePickup')
      case OrderType.TABLE:
        return t('orderTypeTable')
      default:
        return ''
    }
  }

  const generateWhatsAppMessage = (items: CartItem[]) => {
    // Create table-like format for items
    const itemsList = items.map((cartItem, index) => {
      const name = cartItem.item.name
      const quantity = `${cartItem.quantity}x`
      
      // Format as table row without price
      return `${index + 1}. *${name}*\n   ${t('quantity')}: *${quantity}*`
    }).join('\n\n')

    let message = `${t('welcomeMessage')}\n`
    message += `${t('orderType')}: *${getOrderTypeLabel(orderType)}*\n`
    message += `━━━━━━━━━━━━━━━━━━\n\n`
    message += `*${t('customerData')}:*\n`
    message += `━━━━━━━━━━━━━━━━━━\n`
    message += `${t('customerName')}: *${customerName}*\n`
    message += `${t('customerPhone')}: *${customerPhone}*\n`
    if (orderType === OrderType.DELIVERY && customerAddress) {
      message += `${t('enterDeliveryAddress')}: *${customerAddress}*\n\n`
    }
    message += `*${t('orderDetails')}:*\n`
    message += `━━━━━━━━━━━━━━━━━━\n`
    message += `${itemsList}\n\n`
    message += `━━━━━━━━━━━━━━━━━━\n`
    message += `${t('enjoyExperience')}\n`
    message += `${t('thanksForChoosing')} *${businessName}*!\n\n`
    message += `*${t('forMoreOrders')}*\n`
    message += `${window.location.origin}${slug ? AppRoute.MENU_ROUTE(slug) : ''}\n`

    // encodeURIComponent properly handles emojis and UTF-8 characters
    return encodeURIComponent(message)
  }

  const handleSubmit = () => {
    if (!customerName.trim()) {
      alert(t('pleaseEnterName'))
      return
    }
    if (!customerPhone.trim()) {
      alert(t('pleaseEnterWhatsApp'))
      return
    }
    if (orderType === OrderType.DELIVERY && !customerAddress.trim()) {
      alert(t('pleaseEnterDeliveryAddress'))
      return
    }

    if (!businessPhone) {
      alert(t('restaurantWhatsAppNotAvailable'))
      return
    }

    setIsSubmitting(true)

    // Format phone number
    const formattedPhone = "+2" + businessPhone;
    const message = generateWhatsAppMessage(items)

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank')

    // Clear cart and close modals
    setTimeout(() => {
      clearCart()
      setIsSubmitting(false)
      onConfirm()
      onClose()
    }, 500)
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[150]"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-[160] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        {/* Header */}
        <div className="border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
          <div className="px-4 sm:px-6">
            <div className="flex justify-between items-center py-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: opacity15 }}
                >
                  <User className="w-5 h-5" style={{ color }} />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-[#1E293B]">
                    {t('customerInformation')}
                  </h2>
                  <p className="text-xs text-gray-500 leading-tight">
                    {t('enterDetailsToCompleteOrder')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          <div className="space-y-4">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('customerName')} *
              </label>
              <div className="relative">
                <User className={`absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                <Input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={t('customerNamePlaceholder')}
                  className={`${isRTL ? 'pr-10' : 'pl-10'} w-full h-11 border-gray-300`}
                  style={{
                    '--tw-ring-color': color,
                  } as React.CSSProperties}
                  onFocus={(e) => {
                    e.target.style.borderColor = color
                    e.target.style.boxShadow = `0 0 0 2px ${opacity15}`
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db'
                    e.target.style.boxShadow = 'none'
                  }}
                  required
                />
              </div>
            </div>

            {/* Customer Phone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('customerPhone')} *
              </label>
              <div className="relative">
                <Phone className={`absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
                <Input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder={t('customerPhonePlaceholder')}
                  className={`${isRTL ? 'pr-10' : 'pl-10'} w-full h-11 border-gray-300`}
                  onFocus={(e) => {
                    e.target.style.borderColor = color
                    e.target.style.boxShadow = `0 0 0 2px ${opacity15}`
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db'
                    e.target.style.boxShadow = 'none'
                  }}
                  required
                />
              </div>
            </div>

            {/* Order Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                {t('orderType')} *
              </label>
              <div className="flex gap-2">
                {orderTypeOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all flex-1 ${
                      orderType === option.value
                        ? ''
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    style={orderType === option.value ? {
                      borderColor: color,
                      backgroundColor: opacity15,
                    } : {}}
                  >
                    <input
                      type="radio"
                      name="orderType"
                      value={option.value}
                      checked={orderType === option.value}
                      onChange={(e) => setOrderType(e.target.value as OrderType)}
                      className="w-4 h-4 focus:ring-0 focus:ring-offset-0 cursor-pointer flex-shrink-0"
                      style={{ accentColor: color }}
                    />
                    <ShoppingBag 
                      className="w-4 h-4 flex-shrink-0" 
                      style={{ color: orderType === option.value ? color : '#9ca3af' }}
                    />
                    <span 
                      className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}
                      style={{ color: orderType === option.value ? darker : '#374151' }}
                    >
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Customer Address */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('address')}
                {orderType === OrderType.DELIVERY && (
                  <span className="ml-1" style={{ color: darker }}>*</span>
                )}
              </label>
              <div className="relative">
                <MapPin className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 ${orderType === OrderType.DELIVERY ? 'text-gray-400' : 'text-gray-300'}`} />
                <textarea
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder={orderType === OrderType.DELIVERY ? t('enterDeliveryAddress') : t('addressNotRequired')}
                  rows={2}
                  disabled={orderType !== OrderType.DELIVERY}
                  className={`${isRTL ? 'pr-10' : 'pl-10'} w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none ${
                    orderType !== OrderType.DELIVERY 
                      ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-900'
                  }`}
                  onFocus={(e) => {
                    if (orderType === OrderType.DELIVERY) {
                      e.target.style.borderColor = color
                      e.target.style.boxShadow = `0 0 0 2px ${opacity15}`
                    }
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db'
                    e.target.style.boxShadow = 'none'
                  }}
                  required={orderType === OrderType.DELIVERY}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white p-4 bottom-0 shadow-lg">
          <div className="flex gap-2">
            
            <Button
              onClick={handleSubmit}
              variant="primary"
              className="h-10 text-xs font-semibold shadow-md hover:opacity-90 transition-opacity"
              style={{
                background: 'linear-gradient(to right, #25D366, #128C7E)',
              }}
              disabled={isSubmitting || !customerName.trim() || !customerPhone.trim() || (orderType === OrderType.DELIVERY && !customerAddress.trim())}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="loading-spinner w-4 h-4 border-2 border-white/50 border-t-white"></div>
                  {t('sending')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  {t('sendWhatsApp')}
                </span>
              )}
            </Button>

            <Button
              onClick={onClose}
              variant="outline"
              className="h-10 text-ms font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {t('cancel')}
            </Button>
            
          </div>
        </div>
      </div>
    </>
  )
}

